import React, { ComponentType, useEffect, useMemo, useState } from 'react';
import { compose } from 'redux';
import { WidgetType } from '@om-fe/widgets/components/external-sport-widgets/widget.constants';
import { EventMappingRpcResponseItem, GetEventMappingOptions, GetEventMappingRpcResponse } from '@om-fe/api';
import isEmpty from 'lodash/isEmpty';
import { Spinner } from '@om-fe/widgets/dist/components/spinner/spinner';
import { useRpcState } from "@om-fe/widgets/dist/common/rpc/rpc-state.hooks";
import { EventTimeModel, MatchDetailsViewModel, DisciplineLiveStatus } from '@om-fe/models';
import { filterStreamsByLanguage } from '@om-fe/utils/dist/filter-streams-by-language';
import { MatchDetailsHeaderContent } from './match-details-header-content';
import { FALLBACK_LANGUAGE, I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import getConfig from '@om-fe/core/utils/config';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import { connect } from 'react-redux';
import { AppState } from '@om-fe/core/core/types';
import { MatchDetailsStateExt, SLICE_MATCH_DETAILS } from '@om-fe/core/components/match-details/match-details.store';
import getSlice from '@om-fe/core/utils/get-slice';
import { MATCHLIST_GROUP_BEM as bem } from '../match-list-group/match-list-group.constants';

const widgetScoreList: string[] = [
    WidgetType.OMScoreWidget,
    WidgetType.BrWidget,
    WidgetType.BrWidgetLMT,
    WidgetType.StatScoreV2,
];

interface MatchDetailsSidebarBetradarPublicProps {
    readonly matchId: string;
    readonly lang: string;
    readonly disciplineId: string;
    readonly liveStatus?: DisciplineLiveStatus;
}

interface MatchDetailsSidebarBetradarPropsStoreProps {
    readonly hostname?: string;
    readonly event?: MatchDetailsViewModel | null;
    readonly eventTime?: EventTimeModel | null;
    readonly isAuthenForTopic?: boolean;
}

type MatchDetailsSidebarBetradarProps = MatchDetailsSidebarBetradarPublicProps
    & MatchDetailsSidebarBetradarPropsStoreProps
    & I18nContextInterface;

function MatchDetailsSidebarBetradarComponent(props: MatchDetailsSidebarBetradarProps) {
    const {
        matchId,
        disciplineId,
        lang,
        selectedLanguage,
        event,
        eventTime,
        liveStatus,
        isAuthenForTopic,
    } = props;

    const {
        betradar,
        i18nConfig: { defaultLanguage },
        statScoreWidgetV2Config,
    } = getConfig();

    const [scoreWidget, setScoreWidget] = useState<EventMappingRpcResponseItem | null>(null);
    const [streamingWidget, setStreamingWidget] = useState<EventMappingRpcResponseItem | null>(null);
    const clientId: string | undefined = betradar?.clientId;
    const [isEventMappingReady, setIsEventMappingReady] = useState<boolean>(false);
    const hasBetradarWidget: boolean = !!(scoreWidget && (scoreWidget.key === WidgetType.BrWidget || scoreWidget.key === WidgetType.BrWidgetLMT) && clientId);

    const options: GetEventMappingOptions = useMemo(() => {
        return {
            matchId,
            lang,
        }
    }, [lang, matchId]);

    const [{data: eventMapping}] = useRpcState<GetEventMappingRpcResponse>('eventMapping', options);

    useEffect(() => {
        if (!eventMapping) {
            setIsEventMappingReady(true);

            return;
        }

        let scoreWidgetValue: EventMappingRpcResponseItem | null = null;
        let streamingWidgetValue: EventMappingRpcResponseItem | null = null;
        let eventMappingData: GetEventMappingRpcResponse | null = eventMapping;

        if (
            statScoreWidgetV2Config?.group?.hiddenFromRightSidebar
            && eventMappingData.STATS && eventMappingData.STATS[0].key === WidgetType.StatScoreV2
        ) {
            if (
                !statScoreWidgetV2Config?.group?.hiddenWidgetList
                || (Array.isArray(statScoreWidgetV2Config?.group?.hiddenWidgetList) && statScoreWidgetV2Config?.group?.hiddenWidgetList.length === 0)
            ) {
                eventMappingData = Object.fromEntries(Object.entries(eventMappingData).filter(([key]) => key !== 'SCORE'));
            } else {
                for (let x in eventMappingData) {
                    eventMappingData[x] = eventMappingData[x].filter(item => !statScoreWidgetV2Config?.group?.hiddenWidgetList.includes(item.key));
                }
            }
        }

        if (eventMappingData && eventMappingData.SCORE && !isEmpty(eventMappingData.SCORE)) {
            eventMappingData.SCORE.forEach((widget: EventMappingRpcResponseItem) => {
                const { key, value } = widget;
                if (!scoreWidgetValue && widgetScoreList.includes(key)) {
                    scoreWidgetValue = {
                        key,
                        value,
                    }
                }
            });
        }

        if (eventMappingData && eventMappingData.STREAMING && eventMappingData.STREAMING[0]) {
            streamingWidgetValue = (
                filterStreamsByLanguage(
                    eventMappingData.STREAMING,
                    selectedLanguage,
                    defaultLanguage,
                    FALLBACK_LANGUAGE,
                )
            );
        }

        setStreamingWidget(streamingWidgetValue);
        setScoreWidget(scoreWidgetValue);
        setIsEventMappingReady(true);
    }, [defaultLanguage, eventMapping, FALLBACK_LANGUAGE, lang, matchId, selectedLanguage]);

    if (!isEventMappingReady) {
        return <Spinner/>
    }
    
    return (
        <section className={bem('BetRadarWidget')}>
            <MatchDetailsHeaderContent
                scoreWidgetId={scoreWidget}
                hasBetradarWidget={hasBetradarWidget}
                streamingWidget={streamingWidget}
                disciplineId={disciplineId}
                clientId={clientId}
                matchId={matchId}
                event={event}
                eventTime={eventTime}
                liveStatus={liveStatus}
                sidebarShow={true}
                isAuthenForTopic={isAuthenForTopic}
            />
        </section>
    );
}

const MatchDetailsSidebarBetradar: ComponentType<MatchDetailsSidebarBetradarPublicProps> = compose<React.ComponentType<MatchDetailsSidebarBetradarPublicProps>>(
    withTranslation<MatchDetailsSidebarBetradarPublicProps>(),
    connect<MatchDetailsSidebarBetradarPropsStoreProps, undefined, MatchDetailsSidebarBetradarPublicProps, AppState>(
        (state: AppState): MatchDetailsSidebarBetradarPropsStoreProps => {
            const matchSlice: MatchDetailsStateExt | undefined = getSlice(state, SLICE_MATCH_DETAILS);
            const event: MatchDetailsViewModel | null = matchSlice?.event;
            const eventTime: EventTimeModel | null = matchSlice?.eventTime;
            const { enabledTopicDelay } = getConfig();
            const isAuthenForTopic: boolean = !!(enabledTopicDelay && state.session?.info?.isAuthenticated);

            return {
                event,
                eventTime,
                isAuthenForTopic,
            }
        }),
)(MatchDetailsSidebarBetradarComponent);
export default MatchDetailsSidebarBetradar;
